import type { AuthenticatorAssuranceLevels, Session } from '@supabase/supabase-js'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { ClienteEG } from './supabase'

type Nivel = AuthenticatorAssuranceLevels | null

type Estado = {
  /** El cliente de la app (null si falta la configuración). */
  cliente: ClienteEG | null
  /** true mientras se lee la sesión guardada; evita mostrar el acceso por un instante. */
  cargando: boolean
  sesion: Session | null
  /** Nivel de verificación actual (aal2 si ya pasó por MFA; solo importa si el interruptor mfa_personal está encendido). */
  nivelActual: Nivel
  /** true cuando la persona llegó con el enlace de «Olvidé mi contraseña» y debe elegir una nueva. */
  recuperando: boolean
  terminarRecuperacion: () => void
  recargarNivel: () => Promise<void>
  cerrarSesion: () => Promise<void>
}

const Contexto = createContext<Estado | null>(null)

/** Sesión de Supabase compartida por la tienda y el portal. */
export function ProveedorAuth({ cliente, children }: { cliente: ClienteEG | null; children: ReactNode }) {
  const [cargando, setCargando] = useState(cliente !== null)
  const [sesion, setSesion] = useState<Session | null>(null)
  const [nivelActual, setNivelActual] = useState<Nivel>(null)
  const [recuperando, setRecuperando] = useState(false)

  const recargarNivel = useCallback(async () => {
    if (!cliente) return
    const { data } = await cliente.auth.mfa.getAuthenticatorAssuranceLevel()
    setNivelActual(data?.currentLevel ?? null)
  }, [cliente])

  useEffect(() => {
    if (!cliente) return
    let vivo = true
    void cliente.auth.getSession().then(async ({ data }) => {
      if (!vivo) return
      setSesion(data.session)
      if (data.session) await recargarNivel()
      if (vivo) setCargando(false)
    })
    const { data: suscripcion } = cliente.auth.onAuthStateChange((evento, nueva) => {
      setSesion(nueva)
      if (evento === 'PASSWORD_RECOVERY') setRecuperando(true)
      // No se llama al cliente dentro del callback (puede bloquearlo): se difiere.
      if (nueva) setTimeout(() => void recargarNivel(), 0)
      else {
        setNivelActual(null)
        setRecuperando(false)
      }
    })
    return () => {
      vivo = false
      suscripcion.subscription.unsubscribe()
    }
  }, [cliente, recargarNivel])

  const cerrarSesion = useCallback(async () => {
    await cliente?.auth.signOut()
  }, [cliente])
  const terminarRecuperacion = useCallback(() => setRecuperando(false), [])

  const valor = useMemo(
    () => ({ cliente, cargando, sesion, nivelActual, recuperando, terminarRecuperacion, recargarNivel, cerrarSesion }),
    [cliente, cargando, sesion, nivelActual, recuperando, terminarRecuperacion, recargarNivel, cerrarSesion],
  )
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}

export function useAuth(): Estado {
  const c = useContext(Contexto)
  if (!c) throw new Error('useAuth debe usarse dentro de ProveedorAuth')
  return c
}
